import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Save, Trash2, Vote, X, RefreshCw, Eye, EyeOff, Power, BarChart3, Pencil, AlertTriangle } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import AdminCard from "./AdminCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const emptyForm = () => ({
    question: "",
    description: "",
    options: [{ id: "", label: "" }, { id: "", label: "" }],
    multi_select: false,
    is_active: true,
    ends_at: "",
});

function PollForm({ initial, onCancel, onSaved }) {
    const [form, setForm] = useState(initial || emptyForm());
    const [saving, setSaving] = useState(false);

    const updateOption = (idx, label) =>
        setForm((f) => ({ ...f, options: f.options.map((o, i) => i === idx ? { ...o, label } : o) }));

    const addOption = () => setForm((f) => ({ ...f, options: [...f.options, { id: "", label: "" }] }));
    const removeOption = (idx) => setForm((f) => ({ ...f, options: f.options.filter((_, i) => i !== idx) }));

    const submit = async (e) => {
        e.preventDefault();
        if (form.options.filter((o) => o.label.trim()).length < 2) {
            toast.error("Wymagane minimum 2 niepuste opcje.");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                question: form.question.trim(),
                description: (form.description || "").trim(),
                options: form.options
                    .filter((o) => o.label.trim())
                    .map((o) => ({ id: o.id || undefined, label: o.label.trim() })),
                multi_select: !!form.multi_select,
                is_active: !!form.is_active,
                ends_at: form.ends_at || null,
            };
            if (initial?.id) {
                await api.put(`/polls/${initial.id}`, payload);
                toast.success("Sonda zaktualizowana.");
            } else {
                await api.post("/polls", payload);
                toast.success("Sonda utworzona.");
            }
            onSaved();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally { setSaving(false); }
    };

    return (
        <form onSubmit={submit} className="space-y-4" data-testid="poll-form">
            <div className="space-y-2">
                <Label>Pytanie</Label>
                <Input
                    required
                    value={form.question}
                    onChange={(e) => setForm({ ...form, question: e.target.value })}
                    placeholder="Np. Czy popierasz remont elewacji w 2026?"
                    data-testid="poll-form-question"
                />
            </div>
            <div className="space-y-2">
                <Label>Krótki opis (opcjonalnie)</Label>
                <Textarea
                    rows={2}
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Dodatkowy kontekst lub instrukcja dla mieszkańców."
                    data-testid="poll-form-description"
                />
            </div>
            <div className="space-y-2">
                <Label>Opcje odpowiedzi (min. 2)</Label>
                <div className="space-y-2" data-testid="poll-form-options">
                    {form.options.map((opt, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                            <span className="text-xs text-gray-400 w-5 shrink-0 text-right">{idx + 1}.</span>
                            <Input
                                value={opt.label}
                                onChange={(e) => updateOption(idx, e.target.value)}
                                placeholder={`Opcja ${idx + 1}`}
                                data-testid={`poll-form-option-${idx}`}
                                className="flex-1"
                            />
                            {form.options.length > 2 && (
                                <Button
                                    type="button"
                                    onClick={() => removeOption(idx)}
                                    variant="outline"
                                    size="icon"
                                    className="rounded-full text-red-600 hover:bg-red-50 border-red-200 shrink-0"
                                    data-testid={`poll-form-option-remove-${idx}`}
                                >
                                    <Trash2 size={14} />
                                </Button>
                            )}
                        </div>
                    ))}
                </div>
                <Button type="button" onClick={addOption} variant="outline" size="sm" className="rounded-full" data-testid="poll-form-add-option">
                    <Plus size={14} className="mr-1.5" /> Dodaj opcję
                </Button>
            </div>

            <div className="grid md:grid-cols-2 gap-3">
                <label className="flex items-center gap-2 p-3 rounded-xl border border-gray-200 cursor-pointer hover:border-[#0D4F3C]/40">
                    <input
                        type="checkbox"
                        checked={form.multi_select}
                        onChange={(e) => setForm({ ...form, multi_select: e.target.checked })}
                        className="w-4 h-4 accent-[#0D4F3C]"
                        data-testid="poll-form-multi-select"
                    />
                    <div>
                        <div className="text-sm font-medium text-[#092E22]">Wybór wielokrotny</div>
                        <div className="text-[11px] text-gray-500">Mieszkańcy mogą wybrać kilka opcji</div>
                    </div>
                </label>
                <label className="flex items-center gap-2 p-3 rounded-xl border border-gray-200 cursor-pointer hover:border-[#0D4F3C]/40">
                    <input
                        type="checkbox"
                        checked={form.is_active}
                        onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                        className="w-4 h-4 accent-[#0D4F3C]"
                        data-testid="poll-form-active"
                    />
                    <div>
                        <div className="text-sm font-medium text-[#092E22]">Aktywna na stronie głównej</div>
                        <div className="text-[11px] text-gray-500">Wyświetlana w widgecie sondy (tylko jedna naraz)</div>
                    </div>
                </label>
            </div>

            <div className="space-y-2">
                <Label>Data zakończenia (opcjonalnie)</Label>
                <Input
                    type="datetime-local"
                    value={form.ends_at ? form.ends_at.slice(0, 16) : ""}
                    onChange={(e) => setForm({ ...form, ends_at: e.target.value ? new Date(e.target.value).toISOString() : "" })}
                    data-testid="poll-form-ends-at"
                />
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-gray-50">
                <Button type="button" variant="outline" onClick={onCancel} className="rounded-full" data-testid="poll-form-cancel">
                    <X size={14} className="mr-1.5" /> Anuluj
                </Button>
                <Button type="submit" disabled={saving} className="rounded-full bg-[#0D4F3C] hover:bg-[#092E22] text-white" data-testid="poll-form-save">
                    <Save size={14} className="mr-1.5" /> {saving ? "Zapisywanie..." : (initial?.id ? "Zapisz zmiany" : "Utwórz sondę")}
                </Button>
            </div>
        </form>
    );
}

export default function PollsAdmin() {
    const [polls, setPolls] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // null | "new" | poll object

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/polls");
            setPolls(r.data || []);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const activate = async (id) => {
        try {
            await api.patch(`/polls/${id}/activate`);
            toast.success("Sonda aktywna.");
            await load();
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    };
    const close = async (id) => {
        try {
            await api.patch(`/polls/${id}/close`);
            toast.success("Sonda zamknięta.");
            await load();
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    };
    const remove = async (id) => {
        if (!window.confirm("Usunąć tę sondę wraz z wszystkimi głosami?")) return;
        try {
            await api.delete(`/polls/${id}`);
            toast.success("Sonda usunięta.");
            await load();
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    };
    const resetVotes = async (id) => {
        if (!window.confirm("Wyzerować wszystkie głosy w tej sondzie?")) return;
        try {
            await api.post(`/polls/${id}/reset`);
            toast.success("Głosy wyzerowane.");
            await load();
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    };

    return (
        <div className="space-y-6" data-testid="polls-admin">
            <AdminCard>
                <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
                    <div>
                        <h3 className="font-serif text-xl text-[#092E22]">Sondy mieszkańców</h3>
                        <p className="text-sm text-gray-500">Twórz ankiety wyświetlane na stronie głównej. W danym czasie aktywna jest jedna sonda.</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button onClick={load} variant="outline" size="sm" className="rounded-full" data-testid="polls-refresh">
                            <RefreshCw size={14} className="mr-1.5" /> Odśwież
                        </Button>
                        <Button onClick={() => setEditing("new")} className="rounded-full bg-[#0D4F3C] hover:bg-[#092E22] text-white" data-testid="polls-new">
                            <Plus size={14} className="mr-1.5" /> Nowa sonda
                        </Button>
                    </div>
                </div>

                <div className="flex items-start gap-2 mb-5 p-3 bg-blue-50 border border-blue-200 rounded-xl text-xs text-blue-800">
                    <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                    <span>
                        Anti-duplikat: 1 głos na przeglądarkę (cookie/localStorage) + soft-cap 5 głosów z jednego IP (dla rodzin za wspólnym WiFi).
                        Reset głosów wyczyści wszystkie oddane głosy w sondzie.
                    </span>
                </div>

                {editing && (
                    <div className="mb-6 p-5 rounded-2xl border-2 border-[#D4A537]/40 bg-amber-50/30" data-testid="poll-form-container">
                        <h4 className="font-serif text-lg text-[#092E22] mb-4 flex items-center gap-2">
                            <Vote size={18} className="text-[#D4A537]" />
                            {editing === "new" ? "Nowa sonda" : "Edycja sondy"}
                        </h4>
                        <PollForm
                            initial={editing === "new" ? null : editing}
                            onCancel={() => setEditing(null)}
                            onSaved={() => { setEditing(null); load(); }}
                        />
                    </div>
                )}

                {loading && <p className="text-gray-500 text-sm">Wczytywanie...</p>}
                {!loading && polls.length === 0 && !editing && (
                    <p className="text-gray-500 text-sm" data-testid="polls-empty">
                        Brak sond. Utwórz pierwszą za pomocą przycisku „Nowa sonda".
                    </p>
                )}

                <div className="space-y-3">
                    {polls.map((p) => (
                        <article
                            key={p.id}
                            data-testid={`poll-row-${p.id}`}
                            className={`border rounded-2xl p-5 transition-colors ${p.is_active ? "border-emerald-300 bg-emerald-50/30" : "border-gray-100"}`}
                        >
                            <div className="flex flex-wrap items-start gap-3 mb-3">
                                <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold border ${
                                    p.is_active && !p.is_closed
                                        ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                                        : p.is_closed
                                            ? "bg-gray-200 text-gray-700 border-gray-300"
                                            : "bg-amber-100 text-amber-800 border-amber-300"
                                }`}>
                                    {p.is_active && !p.is_closed ? "Aktywna" : p.is_closed ? "Zamknięta" : "Wyłączona"}
                                </span>
                                {p.multi_select && (
                                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                                        wybór wielokrotny
                                    </span>
                                )}
                                <span className="ml-auto text-[11px] text-gray-400 inline-flex items-center gap-1">
                                    <BarChart3 size={12} /> {p.total_votes} głosów
                                </span>
                            </div>
                            <h4 className="font-serif text-lg text-[#092E22] leading-snug break-words mb-2">{p.question}</h4>
                            {p.description && <p className="text-sm text-gray-600 mb-3 break-words">{p.description}</p>}
                            <ul className="space-y-1 text-xs text-gray-600 mb-4">
                                {p.options.map((o) => (
                                    <li key={o.id} className="flex items-center justify-between gap-3">
                                        <span className="truncate">{o.label}</span>
                                        <span className="tabular-nums shrink-0">{o.votes} ({o.percent}%)</span>
                                    </li>
                                ))}
                            </ul>
                            <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-50">
                                {!p.is_active ? (
                                    <Button onClick={() => activate(p.id)} size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`poll-activate-${p.id}`}>
                                        <Power size={14} className="mr-1.5" /> Aktywuj
                                    </Button>
                                ) : (
                                    <Button onClick={() => close(p.id)} size="sm" variant="outline" className="rounded-full" data-testid={`poll-close-${p.id}`}>
                                        <EyeOff size={14} className="mr-1.5" /> Zamknij
                                    </Button>
                                )}
                                <Button onClick={() => setEditing(p)} size="sm" variant="outline" className="rounded-full" data-testid={`poll-edit-${p.id}`}>
                                    <Pencil size={14} className="mr-1.5" /> Edytuj
                                </Button>
                                <Button onClick={() => resetVotes(p.id)} size="sm" variant="outline" className="rounded-full" data-testid={`poll-reset-${p.id}`}>
                                    <RefreshCw size={14} className="mr-1.5" /> Zeruj głosy
                                </Button>
                                <Button onClick={() => remove(p.id)} size="sm" variant="outline" className="rounded-full text-red-600 hover:bg-red-50 border-red-200 ml-auto" data-testid={`poll-delete-${p.id}`}>
                                    <Trash2 size={14} className="mr-1.5" /> Usuń
                                </Button>
                            </div>
                        </article>
                    ))}
                </div>
            </AdminCard>
        </div>
    );
}
